import React from 'react';
import { MapPin, Plus, X } from 'lucide-react';
import {
  canAddDestination,
  removeDestination,
  MAX_VISIT_DESTINATIONS,
} from '../../utils/visitDestinations';
import type { VisitDestinationChoice } from '../../utils/visitDestinations';

/**
 * The destinations of one visit, under the unidade picker.
 *
 * The porteiro picks the first destination the way they always have; this is what lets them say
 * "and also 202, and 303" without registering the same person three times — three people with
 * one CPF, three faces to enrol, three rows to revoke.
 *
 * Owns the LIST, not the search: the modal already knows how to find unidades and departamentos,
 * so adding one calls back to it (`onPick`) rather than duplicating that picker here.
 *
 * Renders nothing when the customer has not enabled the feature.
 */
interface VisitDestinationListProps {
  /** The customer's "Visita a Múltiplos Destinos" setting. */
  enabled: boolean;
  destinations: VisitDestinationChoice[];
  onChange: (destinations: VisitDestinationChoice[]) => void;
  /** Open the host's own unidade/departamento picker. */
  onPick: () => void;
}

export const VisitDestinationList: React.FC<VisitDestinationListProps> = ({
  enabled,
  destinations,
  onChange,
  onPick,
}) => {
  if (!enabled) return null;

  const chosen = destinations ?? [];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <label className="block text-xs font-medium text-gray-700">Outros destinos</label>
        {/* Only worth saying once there IS a visit covering several places — that is what
            decides how many unidades get a visitor, and the porteiro should see it before
            saving. */}
        {chosen.length > 1 && (
          <span className="text-[11px] font-medium text-blue-600">{chosen.length} destinos</span>
        )}
      </div>

      {chosen.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chosen.map(destination => (
            <span
              key={`${destination.kind}:${destination.id}`}
              className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-lg bg-blue-50 border border-blue-100 text-xs text-blue-800"
            >
              <MapPin className="h-3 w-3 text-blue-400" />
              {destination.label}
              <button
                type="button"
                aria-label={`Remover ${destination.label}`}
                onClick={() => onChange(removeDestination(chosen, destination.id))}
                className="p-0.5 rounded text-blue-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={onPick}
        disabled={!canAddDestination(chosen)}
        title={canAddDestination(chosen) ? undefined : `Máximo de ${MAX_VISIT_DESTINATIONS} destinos`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Plus className="h-3.5 w-3.5" />
        Adicionar destino
      </button>
    </div>
  );
};

export default VisitDestinationList;
